import { GD } from "src/GD"
import { Typo } from "@ui/text/Typo"
import { formatCardNumber } from "src/tools/tools"
import { Box, HBox, VBox } from "@ui/box/Box"
import { FC, ReactNode, useEffect, useState, useRef } from "react"
import { VisaIcon } from "src/assets/VisaIcon"
import { MastercardIcon } from "src/assets/MastercardIcon"
import { SignalHandler } from "badmfck-signal"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { Spinner } from "@ui/spinner/Spinner"
import { CARD_BLOCKING_STATUS, getCardDisplayStatus, getCardStatusDisplayLabel, isCardPermanentlyBlocked } from "src/utils/cardStatus"
import { DS, StatGroup } from "../ds/Kit"
import { Error as ErrorNotice } from "@ui/error/Error"
import { findSort, sortQuery, sortRows, textMatches, type SortOption } from "src/utils/listSort"
import { usePagedList } from "../hooks/usePagedList"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { ListPager } from "./ListPager"

type CardsListFilter = {
	order_id: string
	code: string
	status: string
	currency: string
	country: string
	sort?: string
}

export const CARD_SORT_OPTIONS: SortOption<ICardSaved>[] = [
	{ label: "Newest first", sort: "created_at", order: "desc", get: c => c.created_at },
	{ label: "Oldest first", sort: "created_at", order: "asc", get: c => c.created_at },
	{ label: "Name A–Z", sort: "name", order: "asc", get: c => c.name },
	{ label: "Balance: high to low", sort: "balance", order: "desc", get: c => c.balance },
	{ label: "Balance: low to high", sort: "balance", order: "asc", get: c => c.balance },
	{ label: "Status", sort: "status", order: "asc", get: c => c.status },
	// Buyer names are not on card rows; an older backend keeps its own order for this one.
	{ label: "Buyer A–Z", sort: "owner", order: "asc", get: () => undefined }
]

const CARD_PAGE_SIZE = 24

const summarizeCards = (cards: ICardSaved[]): ICardListSummary => ({
	cards: cards.length,
	active: cards.filter(card => card.status === "active").length,
	balance: cards.reduce((sum, card) => sum + (card.balance ?? 0), 0)
})

const CARD_NETWORK_LABELS: Record<number, string> = {
	1: "visa",
	2: "mastercard",
	3: "mastercard"
}

const CARD_LIST_POLL_INTERVAL_MS = 10000
const FRESH_CARD_OVERRIDE_TTL_MS = 60000

type FreshCardOverride = {
	card: ICardSaved
	expiresAt: number
}

const getCardBalance = (card: ICardSaved) => card.providerData?.balance ?? card.balance

const mergeFreshCard = (listedCard: ICardSaved, freshCard: ICardSaved): ICardSaved => ({
	...listedCard,
	...freshCard,
	balance: getCardBalance(freshCard) ?? listedCard.balance,
	providerData: freshCard.providerData ?? listedCard.providerData
})

const reconcileFreshCards = (
	cards: ICardSaved[],
	overrides: Map<string, FreshCardOverride>
) => {
	const now = Date.now()
	return cards.map((listedCard) => {
		const override = overrides.get(listedCard.uid)
		// A list response is also authoritative evidence of permanent closure;
		// it must not require opening card details to protect that status.
		if (isCardPermanentlyBlocked(listedCard.status) && !isCardPermanentlyBlocked(override?.card.status)) {
			overrides.set(listedCard.uid, { card: listedCard, expiresAt: 0 })
			return listedCard
		}
		if (!override) return listedCard
		// Permanent closure cannot be undone by a delayed list response, even
		// after the balance override expires. Do not pin an old balance forever.
		if (isCardPermanentlyBlocked(override.card.status)) {
			const status = isCardPermanentlyBlocked(listedCard.status)
				? listedCard.status : override.card.status
			return override.expiresAt > now && getCardBalance(listedCard) !== getCardBalance(override.card)
				? { ...mergeFreshCard(listedCard, override.card), status }
				: { ...listedCard, status }
		}
		if (override.expiresAt <= now) {
			overrides.delete(listedCard.uid)
			return listedCard
		}
		if (getCardBalance(listedCard) === getCardBalance(override.card) &&
			listedCard.status === override.card.status &&
			Boolean(listedCard.blocking) === Boolean(override.card.blocking)) {
			overrides.delete(listedCard.uid)
			return listedCard
		}
		return mergeFreshCard(listedCard, override.card)
	})
}


// ---- Card UI primitives shared by the list, the details screen and its sub-views ----

/** Small card-shaped tile with the payment network logo. */
export const CardGlyph = ({ network, size = 40 }: { network?: number; size?: number }) => (
	<Box
		aria-hidden
		sx={{
			width: `${size}px`,
			height: `${Math.round(size * 0.68)}px`,
			minWidth: `${size}px`,
			borderRadius: `${Math.max(6, Math.round(size * 0.16))}px`,
			background: "linear-gradient(135deg, #E8F0F8 0%, #FFFFFF 55%, #DCE7F2 100%)",
			border: `1px solid ${DS.line}`,
			boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			"& svg": { width: `${Math.round(size * 0.55)}px`, height: "auto" }
		}}
	>
		{network === 2 || network === 3 ? <MastercardIcon /> : <VisaIcon />}
	</Box>
)

type CardStatusTone = { bg: string; fg: string; line: string }
const CARD_STATUS_TONES = {
	good: { bg: "#ECFDF5", fg: "#047857", line: "#A7F3D0" },
	warn: { bg: "#FFFBEB", fg: "#B45309", line: "#FDE68A" },
	bad: { bg: "#FEF2F2", fg: "#B91C1C", line: "#FECACA" },
	neutral: { bg: DS.soft, fg: DS.muted, line: DS.line }
} satisfies Record<string, CardStatusTone>

const getCardStatusTone = (status?: string | null): CardStatusTone => {
	const value = (status || "").toLowerCase()
	if (["active", "approved"].includes(value)) return CARD_STATUS_TONES.good
	if (["created", "pending", "frozen", "processing", CARD_BLOCKING_STATUS].includes(value)) return CARD_STATUS_TONES.warn
	if (["blocked", "closed", "lost", "declined", "error", "unavailable", "replaced"].includes(value)) return CARD_STATUS_TONES.bad
	return CARD_STATUS_TONES.neutral
}

/** Status pill. The DOM text stays as the API status; only the first letter is capitalised visually. */
export const CardStatusPill = ({ status }: { status?: string | null }) => {
	const tone = getCardStatusTone(status)
	return (
		<HBox gap='6px' sx={{ alignItems: "center", width: "fit-content", padding: "3px 10px", borderRadius: "999px", border: `1px solid ${tone.line}`, background: tone.bg, color: tone.fg, fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>
			<Box aria-hidden sx={{ width: "6px", height: "6px", borderRadius: "50%", background: tone.fg }} />
			<Box element='span' sx={{ display: "inline-block", "&::first-letter": { textTransform: "uppercase" } }}>{getCardStatusDisplayLabel(status)}</Box>
		</HBox>
	)
}

/** Consistent screen for card sub-views: title + explainer, card context strip, optional stats, then content. */
export const CardSubView = ({ title, description, card, masked, stats, onBack, backTitle = "Back", children }: {
	title: string
	description: string
	card?: Pick<ICardSaved, "name" | "mask" | "network" | "status" | "external_number" | "blocking">
	masked?: string | null
	stats?: { label: string; value: ReactNode; hint?: ReactNode }[]
	onBack?: () => void
	backTitle?: string
	children: ReactNode
}) => (
	<VBox gap='16px' sx={{ width: "100%", minWidth: 0 }}>
		{onBack && (
			<Box
				element='button'
				type='button'
				onClick={onBack}
				sx={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "4px", border: 0, background: "transparent", color: DS.muted, fontFamily: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer", padding: "6px 8px 6px 0", minHeight: "44px", "&:hover": { color: DS.ink } }}
			>
				<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='m15 18-6-6 6-6' /></svg>
				{backTitle}
			</Box>
		)}
		<VBox gap='4px' sx={{ paddingRight: "48px", minWidth: 0 }}>
			<Typo sx={{ fontSize: "20px", fontWeight: 700, color: DS.ink, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{title}</Typo>
			<Typo sx={{ fontSize: "14px", color: DS.muted, lineHeight: 1.5 }}>{description}</Typo>
		</VBox>
		{card && (
			<HBox gap='12px' sx={{ alignItems: "center", padding: "12px 16px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.soft, minWidth: 0 }}>
				<CardGlyph network={card.network} size={44} />
				<VBox gap='2px' sx={{ minWidth: 0, flex: 1 }}>
					<Typo sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink, overflowWrap: "anywhere" }}>{card.name || "Virtual card"}</Typo>
					<Typo sx={{ fontSize: "13px", color: DS.muted, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{masked ?? (card.mask || (card.external_number ? formatCardNumber(card.external_number) : ""))}</Typo>
				</VBox>
				{card.status && <CardStatusPill status={getCardDisplayStatus(card)} />}
			</HBox>
		)}
		{stats && <StatGroup items={stats} />}
		{children}
	</VBox>
)

/** Card face colour follows the status tone so the grid reads at a glance. */
const getCardFace = (status?: string | null) => {
	const tone = getCardStatusTone(status)
	if (tone === CARD_STATUS_TONES.bad) return "linear-gradient(135deg, #A7B0BD 0%, #848E9C 100%)"
	if (tone === CARD_STATUS_TONES.warn) return "linear-gradient(135deg, #B45309 0%, #92400E 100%)"
	return DS.heroGradient
}

/** "5177****3217" / "************5644" -> "5177 •••• •••• 3217" / "•••• •••• •••• 5644". Never reveals more digits than the source. */
export const formatCardFaceNumber = (value?: string | null) => {
	const raw = (value || "").replace(/\s/g, "")
	const head = raw.match(/^\d{4}/)?.[0]
	const tail = raw.match(/\d{4}$/)?.[0]
	if (!tail) return "•••• •••• •••• ••••"
	return [head && raw.length > 8 ? head : "••••", "••••", "••••", tail].join(" ")
}

const formatCardListDate = (value?: string | Date | null) => {
	if (!value) return "—"
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return "—"
	return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

export const CardsList: FC<{
	userUid: string
	/** @deprecated not used; the backend scopes cards to the signed-in company. */
	companyUid?: string
	dependency: number
	filter: CardsListFilter
	onSelect?: (card: ICardSaved) => void
	selectedId?: string
	hideBuyer?: boolean
	view?: "grid" | "list"
	/** Totals of every card that matches the filters, not just the page. */
	onSummary?: (summary: ICardListSummary | null) => void
}> = ({ userUid, dependency, filter, onSelect, selectedId, hideBuyer, view = "list", onSummary }) => {
	const selectedIssuerId = useOptionalIssuer()?.selectedIssuerId ?? null
	const [users, updateUsers] = GD.REQ_USERS.useRequest()
	const [debouncedFilter, setDebouncedFilter] = useState(filter)
	const freshCardOverrides = useRef(new Map<string, FreshCardOverride>())

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedFilter(filter)
		}, 500)

		return () => clearTimeout(timeout)
	}, [filter])

	const sort = findSort(CARD_SORT_OPTIONS, debouncedFilter.sort)
	const search = debouncedFilter.order_id.trim()
	const status = debouncedFilter.status
	const { code, currency, country } = debouncedFilter
	const list = usePagedList<ICardSaved, ICardListSummary, ICardListQuery>({
		request: query => GD.REQ_CARDS_PAGE.request(query),
		query: {
			...(selectedIssuerId === null ? {} : { issuer: selectedIssuerId }),
			...(search ? { search } : {}),
			...(status ? { status } : {}),
			...(userUid ? { user_uid: userUid } : {}),
			...sortQuery(sort)
		},
		pageSize: CARD_PAGE_SIZE,
		// The backend has no network, currency or country filters.
		extra: code || currency || country ? {
			key: [code, currency, country].join("|"),
			match: card =>
				(!code || CARD_NETWORK_LABELS[card.network] === code.toLowerCase()) &&
				(!currency || card.currency === currency) &&
				(!country || card.country_code === country)
		} : null,
		legacy: cards => sortRows(cards.filter(card =>
			(!userUid || card.user_uid === userUid) &&
			(!status || card.status === status) &&
			textMatches(search, [card.uid, card.order_id, card.name, card.mask, card.external_number, card.bin])
		), sort),
		summarize: summarizeCards,
		transform: cards => reconcileFreshCards(cards, freshCardOverrides.current)
	})
	const { reload, patchRows } = list
	const rowsRef = useRef(list.rows)
	rowsRef.current = list.rows

	useEffect(() => {
		onSummary?.(list.summary)
	}, [list.summary, onSummary])

	useEffect(() => {
		const sh = new SignalHandler()
		// Another screen changed cards; the payload is the whole list, so read this page again.
		sh.add(GD.S_CARDS_LIST_UPDATED, () => void reload())
		sh.add(GD.S_CARD_UPDATED, (freshCard) => {
			const knownCard = freshCardOverrides.current.get(freshCard.uid)?.card ??
				rowsRef.current.find(card => card.uid === freshCard.uid)
			const nextCard = isCardPermanentlyBlocked(knownCard?.status) && !isCardPermanentlyBlocked(freshCard.status)
				? { ...freshCard, status: knownCard!.status } : freshCard
			freshCardOverrides.current.set(freshCard.uid, {
				card: nextCard,
				expiresAt: Date.now() + FRESH_CARD_OVERRIDE_TTL_MS
			})
			patchRows(cards => reconcileFreshCards(cards, freshCardOverrides.current))
		})

		return () => {
			sh.clear()
		}
	}, [reload, patchRows])

	const firstDependency = useRef(dependency)
	useEffect(() => {
		updateUsers()
		if (dependency !== firstDependency.current) void reload()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dependency])

	useEffect(() => {
		const timer = window.setInterval(() => void reload(), CARD_LIST_POLL_INTERVAL_MS)
		return () => window.clearInterval(timer)
	}, [reload])

	if (list.initialLoading) {
		return (
			<Box
				role='status'
				aria-label='Loading card balances'
				sx={{
					padding: "32px 24px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: DS.muted
				}}
			>
				<Spinner color={DS.accent} />
				<Typo variant='medium'>Loading cards and live balances…</Typo>
			</Box>
		)
	}

	if (list.error && !list.rows.length) {
		return <ErrorNotice error={list.error} sx={{ margin: "16px" }} />
	}

	if (!list.rows.length) {
		return (
			<Box
				sx={{
					padding: "24px",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%"
				}}
			>
				<Typo variant='medium' sx={{ color: DS.muted }}>
					No cards found.
				</Typo>
			</Box>
		)
	}

	const filteredCards = list.rows
	const withPager = (content: ReactNode) => (
		<VBox aria-busy={list.loading} sx={{ height: "100%", minHeight: 0, [DS.mobile]: { height: "auto" } }}>
			<Box sx={{ flex: 1, minHeight: 0, opacity: list.loading ? 0.6 : 1, transition: "opacity .15s ease", [DS.mobile]: { flex: "none" } }}>
				{content}
			</Box>
			<ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} label='Cards' />
		</VBox>
	)

	const showBuyer = !userUid && !hideBuyer

	if (view === "grid") {
		return withPager(
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
					gap: "16px",
					padding: "4px 4px 16px",
					alignContent: "start",
					height: "100%",
					overflowY: "auto",
					[DS.mobile]: { padding: "0 0 12px", height: "auto", overflow: "visible" }
				}}
			>
				{filteredCards.map((row) => {
					const user = (users?.data as IUserSaved[])?.find((item) => item.uid === row.user_uid)
					const hasBalance = row.balance !== undefined && row.balance !== null
					const isSelected = selectedId === row.uid
					return (
						<Box
							key={row.uid}
							element='button'
							type='button'
							aria-label={`Open card ${row.name || row.mask || row.uid}`}
							onClick={() => onSelect?.(row)}
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: "12px",
								padding: "12px",
								borderRadius: "20px",
								border: `1px solid ${isSelected ? DS.accent : DS.line}`,
								background: DS.surface,
								boxShadow: DS.shadow,
								fontFamily: "inherit",
								textAlign: "left",
								color: DS.ink,
								cursor: onSelect ? "pointer" : "default",
								transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
								"&:hover": { borderColor: "#B9CDE2", boxShadow: "0 12px 28px rgba(30, 58, 95, 0.12)", transform: "translateY(-2px)" },
								"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" }
							}}
						>
							<VBox
								sx={{
									position: "relative",
									justifyContent: "space-between",
									height: "158px",
									padding: "16px 18px",
									borderRadius: "14px",
									overflow: "hidden",
									color: "#FFFFFF",
									background: getCardFace(getCardDisplayStatus(row)),
									"&::after": { content: '""', position: "absolute", right: "-40px", top: "-60px", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }
								}}
							>
								<HBox sx={{ alignItems: "flex-start", justifyContent: "space-between", gap: "12px", minWidth: 0 }}>
									<Typo sx={{ fontSize: "14px", fontWeight: 650, color: "#FFFFFF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
										{row.name || "Card"}
									</Typo>
									<Box sx={{ flexShrink: 0, padding: "3px 6px", borderRadius: "6px", background: "#FFFFFF", display: "flex", "& svg": { width: "30px", height: "auto" } }}>
										{row.network === 2 || row.network === 3 ? <MastercardIcon /> : <VisaIcon />}
									</Box>
								</HBox>
								<Typo sx={{ fontSize: "16px", letterSpacing: "0.12em", color: "rgba(255,255,255,0.92)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "nowrap" }}>
									{formatCardFaceNumber(row.mask || row.external_number)}
								</Typo>
								<HBox sx={{ alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
									<VBox gap='1px'>
										<Typo sx={{ fontSize: "11px", color: "rgba(255,255,255,0.7)" }}>Balance</Typo>
										<Typo sx={{ fontSize: "20px", fontWeight: 700, color: "#FFFFFF", fontVariantNumeric: "tabular-nums", lineHeight: 1.15 }}>
											{hasBalance ? formatCurrencyWithNumberToSymbolWithNumber(row.balance as number, row.currency || "EUR") : "—"}
										</Typo>
									</VBox>
									{row.balance_type && (
										<Box element='span' sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "999px", background: "rgba(255,255,255,0.18)", color: "#FFFFFF", whiteSpace: "nowrap", "&::first-letter": { textTransform: "uppercase" } }}>
											{String(row.balance_type).replaceAll("_", " ")}
										</Box>
									)}
								</HBox>
							</VBox>
							<HBox sx={{ alignItems: "center", gap: "10px", padding: "0 4px 2px", minWidth: 0 }}>
								<VBox gap='1px' sx={{ minWidth: 0, flex: 1 }}>
									<Typo sx={{ fontSize: "13px", fontWeight: 600, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{showBuyer ? user?.name || "—" : `BIN ${row.bin || "—"}`}
									</Typo>
									<Typo sx={{ fontSize: "12px", color: DS.muted, whiteSpace: "nowrap" }}>
										{[showBuyer && row.bin && `BIN ${row.bin}`, row.country_code, formatCardListDate(row.created_at)].filter(Boolean).join(" · ")}
									</Typo>
								</VBox>
								<CardStatusPill status={getCardDisplayStatus(row)} />
							</HBox>
						</Box>
					)
				})}
			</Box>
		)
	}

	const columns = showBuyer
		? "minmax(190px, 2fr) minmax(100px, 1.2fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(100px, .8fr) minmax(100px, .8fr)"
		: "minmax(190px, 2fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(100px, .8fr) minmax(100px, .8fr)"
	const STACKED = "@container (max-width: 767px)"
	const headers = [
		{ label: "Card" },
		...(showBuyer ? [{ label: "Buyer" }] : []),
		{ label: "BIN · Country" },
		{ label: "Balance", end: true },
		{ label: "Status" },
		{ label: "Created" }
	]
	// On narrow containers every cell names itself via a CSS label, so the text exists only once in the DOM.
	const cellSx = (label: string, extra: Record<string, unknown> = {}) => ({
		minWidth: 0,
		fontSize: "14px",
		color: DS.text,
		[STACKED]: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: "12px",
			"&::before": { content: `"${label}"`, fontSize: "12px", color: DS.muted, flexShrink: 0 }
		},
		...extra
	})

	return withPager(
		<Box
			sx={{
				height: "100%",
				containerType: "inline-size",
				overflow: "auto",
				backgroundColor: DS.surface,
				[DS.mobile]: { height: "auto", overflow: "visible" }
			}}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: columns,
					alignItems: "center",
					gap: "16px",
					padding: "10px 20px",
					position: "sticky",
					top: 0,
					zIndex: 1,
					backgroundColor: DS.soft,
					borderBottom: `1px solid ${DS.line}`,
					[STACKED]: { display: "none" }
				}}
			>
				{headers.map(header => (
					<Typo key={header.label} sx={{ fontSize: "12px", fontWeight: 600, color: DS.muted, textAlign: header.end ? "right" : "left" }}>
						{header.label}
					</Typo>
				))}
			</Box>
			<VBox gap='0px' sx={{ [STACKED]: { padding: "10px", gap: "10px" } }}>
				{filteredCards.map((row) => {
					const user = (users?.data as IUserSaved[])?.find((item) => item.uid === row.user_uid)
					const isSelected = selectedId === row.uid
					const hasBalance = row.balance !== undefined && row.balance !== null
					const balance = hasBalance
						? formatCurrencyWithNumberToSymbolWithNumber(row.balance as number, row.currency || "EUR")
						: "—"

					return (
						<Box
							key={row.uid}
							element='button'
							type='button'
							aria-label={`Open card ${row.name || row.mask || row.uid}`}
							onClick={() => onSelect?.(row)}
							sx={{
								width: "100%",
								display: "grid",
								gridTemplateColumns: columns,
								alignItems: "center",
								gap: "16px",
								padding: "14px 20px",
								border: 0,
								borderBottom: `1px solid ${DS.line}`,
								backgroundColor: isSelected ? DS.accentSoft : DS.surface,
								color: DS.ink,
								fontFamily: "inherit",
								textAlign: "left",
								cursor: onSelect ? "pointer" : "default",
								transition: "background-color .15s ease, box-shadow .15s ease",
								"&:hover": { backgroundColor: onSelect ? (isSelected ? DS.accentSoft : DS.soft) : undefined },
								"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" },
								[STACKED]: {
									gridTemplateColumns: "minmax(0, 1fr)",
									gap: "10px",
									padding: "14px 16px",
									border: `1px solid ${isSelected ? DS.accent : DS.line}`,
									borderRadius: DS.radius,
									boxShadow: DS.shadow
								}
							}}
						>
							<HBox gap='12px' sx={{ alignItems: "center", minWidth: 0 }}>
								<CardGlyph network={row.network} />
								<VBox gap='2px' sx={{ minWidth: 0 }}>
									<Typo
										sx={{
											fontSize: "14px",
											fontWeight: 700,
											color: DS.ink,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											[STACKED]: { whiteSpace: "normal", overflowWrap: "anywhere", textOverflow: "clip" }
										}}
									>
										{row.name || "Card"}
									</Typo>
									<Typo sx={{ fontSize: "13px", color: DS.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
										{formatCardFaceNumber(row.mask || row.external_number)}
									</Typo>
								</VBox>
							</HBox>

							{showBuyer && (
								<Box sx={cellSx("Buyer")}>
									<Typo sx={{ fontSize: "14px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{user?.name || "—"}
									</Typo>
								</Box>
							)}

							<Box sx={cellSx("BIN · Country")}>
								<Typo sx={{ fontSize: "14px", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
									{row.bin || "—"} · {row.country_code || "—"}
								</Typo>
							</Box>

							<Box sx={cellSx("Balance", { textAlign: "right" })}>
								<VBox gap='2px' sx={{ alignItems: "flex-end", [STACKED]: { alignItems: "flex-end" } }}>
									<Typo sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
										{balance}
									</Typo>
									{row.balance_type && (
										<Box element='span' sx={{ fontSize: "11px", fontWeight: 600, color: row.balance_type === "shared" ? "#0F766E" : "#7E22CE", "&::first-letter": { textTransform: "uppercase" } }}>
											{String(row.balance_type).replaceAll("_", " ")}
										</Box>
									)}
								</VBox>
							</Box>

							<Box sx={cellSx("Status")}>
								<CardStatusPill status={getCardDisplayStatus(row)} />
							</Box>

							<Box sx={cellSx("Created", { color: DS.muted })}>
								<Typo sx={{ fontSize: "14px", whiteSpace: "nowrap" }}>{formatCardListDate(row.created_at)}</Typo>
							</Box>
						</Box>
					)
				})}
			</VBox>
		</Box>
	)
}
